import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import WhatsappCta from "../../../components/whatsapp-cta";

/*
  ЛИПКАЯ ПОЛОСА «ЗАБРОНИРОВАТЬ» НА ТЕЛЕФОНЕ.

  Зачем. На странице тура кнопка заявки стоит в середине: после цен, но перед
  блоком описаний. Описаний бывает сколько угодно, на телефоне это несколько
  экранов — человек, дочитавший тур до конца, оставался вообще без кнопки и
  должен был листать обратно вверх.

  Как ведёт себя. Полоса появляется только тогда, когда обычная кнопка уехала
  с экрана, — пока она видна, двух одинаковых кнопок рядом не будет.
  На больших экранах полосы нет: там кнопка и так остаётся в поле зрения.

  Что можно крутить:
    z-40 — ниже запасной формы заявки (z-50), чтобы затемнение перекрывало
    полосу, если WhatsApp не настроен.
*/
export default function StickyBookBar({
  anchorRef,
  subject,
  price,
  details = [],
  presetComment = "",
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;

    // Полоса нужна только ПОСЛЕ того, как обычную кнопку пролистали вниз.
    // Проверять одно !isIntersecting мало: при открытии страницы кнопка тоже
    // «не видна» — она просто ещё ниже экрана, и полоса выезжала сразу,
    // перекрывая контент до того, как человек дошёл до цен.
    const observer = new IntersectionObserver(
      ([entry]) =>
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { rootMargin: "0px 0px -80px 0px" }
    );
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [anchorRef]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      // pb с env(safe-area-inset-bottom) — чтобы полоса не пряталась под
      // домашней чертой на iPhone (в index.html стоит viewport-fit=cover)
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-kj-line bg-kj-dark/95 backdrop-blur-md transition-transform duration-300 ease-out motion-reduce:transition-none ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!visible}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-kj-sub">
            {t("actInfo.price")}
          </div>
          <div className="truncate text-base font-extrabold text-kj-gold">
            {price ? t("actInfo.priceFrom", { price }) : ""}
          </div>
        </div>

        <WhatsappCta
          subject={subject}
          details={details}
          extra={presetComment}
          intent="book"
          label={t("request.book")}
          className="shrink-0 px-6 bg-kj-gold text-kj-dark hover:bg-kj-gold/90 uppercase tracking-[0.08em] text-sm font-bold rounded-sm"
          // полоса уехала вниз — её кнопка не должна ловить фокус с клавиатуры
          tabIndex={visible ? 0 : -1}
        />
      </div>
    </div>,
    document.body
  );
}
